import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import CyberBackground from "../components/CyberBackground";
import ArticleBlocks from "../components/resource/ArticleBlocks";
import CTABand from "../components/resource/CTABand";
import { RESOURCES } from "../i18n/resources";
import { Calendar, Tag, ArrowLeft, ArrowRight } from "lucide-react";

export default function BlogDetail() {
  const { slug } = useParams();
  const { t, dir, lang } = useLang();
  const navigate = useNavigate();
  const flip = dir === "rtl" ? "scaleX(-1)" : "none";
  const blogs = RESOURCES[lang].blogs;
  const blog = blogs.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!blog) navigate("/blog");
  }, [blog, navigate, slug]);

  if (!blog) return null;
  const others = blogs.filter((b) => b.slug !== slug);

  return (
    <div>
      <section className="relative pt-36 pb-14 overflow-hidden">
        <div className="absolute inset-0 rq-grid-bg opacity-40" />
        <div className="absolute inset-0 opacity-60"><CyberBackground /></div>
        <div className="rq-container relative z-10 max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-[color:var(--rq-green)] mb-6" data-testid="blog-back-link">
            <ArrowLeft size={16} style={{ transform: flip }} />
            {t.res.backBlog}
          </Link>
          <div className="flex flex-wrap items-center gap-4 text-xs text-[color:var(--rq-muted)] mb-4">
            <span className="inline-flex items-center gap-1.5"><Tag size={14} className="text-[color:var(--rq-green)]" />{blog.category}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-[color:var(--rq-green)]" />{blog.date}</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">{blog.title}</h1>
        </div>
      </section>

      <section className="pb-8">
        <div className="rq-container max-w-4xl">
          <div className="rq-card overflow-hidden">
            <img src={blog.image} alt={blog.title} className="w-full h-[280px] md:h-[440px] object-cover" />
          </div>
        </div>
      </section>

      <section className="py-10 rq-light">
        <div className="rq-container max-w-3xl">
          <p className="text-lg leading-relaxed text-white font-medium mb-6">{blog.excerpt}</p>
          <ArticleBlocks blocks={blog.blocks} />
        </div>
      </section>

      <section className="py-14">
        <div className="rq-container">
          <h2 className="font-display text-2xl font-bold text-white mb-8">{t.res.relatedBlogs}</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {others.map((b) => (
              <Link key={b.slug} to={`/blog/${b.slug}`} className="rq-card overflow-hidden flex group" data-testid={`related-blog-${b.slug}`}>
                <img src={b.image} alt={b.title} className="w-32 md:w-44 object-cover shrink-0" />
                <div className="p-5">
                  <span className="text-xs font-semibold text-[color:var(--rq-green)]">{b.category}</span>
                  <h3 className="font-display mt-2 text-base font-semibold text-white leading-snug">{b.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--rq-green)]">
                    {t.res.readMore}
                    <ArrowRight size={14} style={{ transform: flip }} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
